import type { InvitationSort, InvitationStatus } from "@unbox/api";
import { useAdminInvites } from "@/api/admin/invites";
import { usePagination } from "@/api/pagination";
import { LogoStack } from "@/components/admin/logo-stack";
import { InviteRoleLabel } from "@/components/admin/role-label";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { Avatar } from "@/components/base/avatar/avatar";
import { Badge } from "@/components/base/badges/badges";
import { useCache } from "@/hooks/use-cache";
import { useSort } from "@/hooks/use-sort";
import { RevokeInviteModal } from "@/pages/admin/modals/revoke-invite";
import { ResendInviteModal } from "../modals/resend-invite";

const StatusLabel: Record<InvitationStatus | "expired", React.ReactNode> = {
  pending: <Badge color="blue-light">Pending</Badge>,
  accepted: <Badge color="success">Accepted</Badge>,
  revoked: <Badge color="error">Revoked</Badge>,
  expired: <Badge color="warning">Expired</Badge>,
};

const getEmailInitials = (email: string) => {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export const AdminInvitesTable = ({ search }: { search: string }) => {
  const pagination = usePagination();
  const sort = useSort<InvitationSort>("createdAt");
  const { data } = useAdminInvites(pagination.pagination, sort.sort, { text: search });
  const total = useCache(data?.total);

  return (
    <TableCard.Root className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table
          aria-label="Team invites"
          selectionMode="none"
          sortDescriptor={sort.descriptor}
          onSortChange={sort.setFromDescriptor}
        >
          <Table.Header>
            <Table.Head id="email" label="Name" isRowHeader allowsSorting />
            <Table.Head id="workspaces" label="Workspaces" />
            <Table.Head id="merchants" label="Individual merchants" />
            <Table.Head id="role" label="Role" />
            <Table.Head id="status" label="Invite Status" allowsSorting />
            <Table.Head id="createdAt" label="Invited" allowsSorting />
            <Table.Head id="expiresAt" label="Expiry date" allowsSorting />
            <Table.Head id="actions" label="" />
          </Table.Header>

          <Table.Body items={data?.invitations ?? []}>
            {(item) => {
              const status =
                item.status === "pending" && Date.now() > new Date(item.expiresAt).getTime() ? "expired" : item.status;
              return (
                <Table.Row id={item.id}>
                  <Table.Cell>
                    <div className="flex items-center gap-3">
                      <Avatar size="sm" initials={getEmailInitials(item.email)} alt={item.email} />
                      <div className="whitespace-nowrap">
                        <p className="text-sm font-medium text-primary">{item.email}</p>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="min-w-[160px]">
                    <LogoStack items={item.workspaces} />
                  </Table.Cell>
                  <Table.Cell className="min-w-[160px]">
                    <LogoStack items={item.merchants} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <InviteRoleLabel assignableRole={item.assignableRole} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{StatusLabel[status]}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{new Date(item.createdAt).toLocaleDateString()}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap">{new Date(item.expiresAt).toLocaleDateString()}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <ResendInviteModal invite={item} />
                    <RevokeInviteModal invite={item} />
                  </Table.Cell>
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
      </div>
      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
    </TableCard.Root>
  );
};
